import { useEffect, useRef, useState } from 'react'
import { Stack, Tab, Tabs } from '@mui/material'
import { PrecisionManufacturingOutlined } from '@mui/icons-material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import LoadMachineStoragePanel from './LoadMachineStoragePanel'
import WithdrawMachineStoragePanel from './WithdrawMachineStoragePanel'

const LOAD_TAB = 0

// Single entry point for the Machine Storage material operations on a Raw Material Reference: one
// modal with two tabs — Load (into a Machine Storage) and Unload (return to the general stock). Each
// tab is a self-contained panel; the dialog's accept button submits the active tab (its `submit`
// exposed via ref). Only the active panel is mounted, so switching tabs starts a fresh operation.
const MachineStorageDialog = ({ open = false, reference = {}, onClose = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const [tab, setTab] = useState(LOAD_TAB)
  const [loading, setLoading] = useState(false)
  const activePanel = useRef(null)

  // Always reopen on the Load tab so each opening is a predictable, fresh operation.
  useEffect(() => {
    if (open) {
      setTab(LOAD_TAB)
      setLoading(false)
    }
  }, [open])

  const handleAccept = () => {
    if (activePanel.current) activePanel.current.submit()
  }

  return (
    <SmartDialog
      title={createDialogTitle({ color: 'primary', title: t_('Machine storage'), icon: PrecisionManufacturingOutlined })}
      setOpen={open}
      cancelCallback={onClose}
      acceptCallback={handleAccept}
      disableButtons={loading}
      renderComponent={
        <Stack spacing={2} sx={{ minWidth: '24rem' }}>
          <Tabs value={tab} onChange={(event, value) => setTab(value)} variant='fullWidth'>
            <Tab label={t_('Load')} id='machine-storage-tab-0' aria-controls='machine-storage-panel-0' />
            <Tab label={t_('Unload')} id='machine-storage-tab-1' aria-controls='machine-storage-panel-1' />
          </Tabs>
          {tab === LOAD_TAB
            ? (
              <LoadMachineStoragePanel
                ref={activePanel}
                open={open}
                reference={reference}
                onClose={onClose}
                onLoadingChange={setLoading}
              />
              )
            : (
              <WithdrawMachineStoragePanel
                ref={activePanel}
                open={open}
                reference={reference}
                onClose={onClose}
                onLoadingChange={setLoading}
              />
              )}
        </Stack>
      }
    />
  )
}

export default MachineStorageDialog
